import { useCallback, useState } from 'react';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import type { AffiliateReferralCode } from '@wlb/shared/api/affiliate/affiliateTypes';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { FormError } from '../../components/ui/FormError';
import { IconButton } from '../../components/ui/IconButton';
import { Modal } from '../../components/ui/Modal';
import { StatusPill } from '../../components/ui/StatusPill';
import { TextField } from '../../components/ui/TextField';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { formatCount, formatCurrency, formatDate } from '../../utils/format';
import '../../components/ui/ui.css';

interface ReferralCodesDialogProps {
  affiliateUserId: number;
  affiliateEmail: string;
  onClose: () => void;
}

export function ReferralCodesDialog({
  affiliateUserId,
  affiliateEmail,
  onClose,
}: ReferralCodesDialogProps) {
  const { t } = useTranslation('admin');
  const [editing, setEditing] = useState<AffiliateReferralCode | null>(null);
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [commission, setCommission] = useState('0');
  const [errors, setErrors] = useState<{ code?: string; commission?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<AffiliateReferralCode | null>(
    null,
  );

  const load = useCallback(
    () => affiliateService.getReferralCodes(affiliateUserId),
    [affiliateUserId],
  );
  const { data, status, error, reload } = useAsyncResource(
    load,
    String(affiliateUserId),
    t('common.errorTitle'),
  );

  const resetForm = () => {
    setEditing(null);
    setCode('');
    setDescription('');
    setCommission('0');
    setErrors({});
  };

  const startEdit = (row: AffiliateReferralCode) => {
    setEditing(row);
    setCode(row.code);
    setDescription(row.description);
    setCommission(String(row.commissionPerDownload));
    setErrors({});
  };

  const submit = async () => {
    const nextErrors: { code?: string; commission?: string } = {};
    if (!code.trim()) {
      nextErrors.code = t('affiliates.referralCodes.codeRequired');
    }
    const amount = Number(commission);
    // An empty string coerces to 0, so the blank case is rejected explicitly.
    if (commission.trim() === '' || !Number.isFinite(amount) || amount < 0) {
      nextErrors.commission = t('affiliates.referralCodes.commissionInvalid');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitError(null);
    setPending(true);
    try {
      if (editing) {
        await affiliateService.updateReferralCode({
          affiliateReferralCodeId: editing.id,
          affiliateUserId,
          referralCode: code.trim(),
          description: description.trim(),
        });
        ToastService.show({
          type: 'success',
          message: t('affiliates.referralCodes.toasts.updated'),
        });
      } else {
        await affiliateService.createReferralCode({
          affiliateUserId,
          referralCode: code.trim(),
          description: description.trim(),
          commissionPerDownload: amount,
        });
        ToastService.show({
          type: 'success',
          message: t('affiliates.referralCodes.toasts.created'),
        });
      }
      resetForm();
      reload();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setPending(false);
    }
  };

  const remove = async (row: AffiliateReferralCode) => {
    setSubmitError(null);
    setPending(true);
    try {
      await affiliateService.deleteReferralCode(row.id, affiliateUserId);
      ToastService.show({
        type: 'success',
        message: t('affiliates.referralCodes.toasts.deleted'),
      });
      if (editing?.id === row.id) {
        resetForm();
      }
      setConfirmRemove(null);
      reload();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setPending(false);
    }
  };

  const columns: readonly DataTableColumn<AffiliateReferralCode>[] = [
    {
      key: 'code',
      header: t('affiliates.referralCodes.columns.code'),
      render: row => <span className="ui-table__mono">{row.code || t('common.none')}</span>,
    },
    {
      key: 'description',
      header: t('affiliates.referralCodes.columns.description'),
      render: row => (
        <span
          className="ui-table__truncate ui-table__truncate--sm"
          title={row.description || undefined}>
          {row.description || t('common.none')}
        </span>
      ),
    },
    {
      key: 'commission',
      header: t('affiliates.referralCodes.columns.commission'),
      numeric: true,
      render: row => formatCurrency(row.commissionPerDownload),
    },
    {
      key: 'downloads',
      header: t('affiliates.referralCodes.columns.downloads'),
      numeric: true,
      render: row => formatCount(row.usageCount),
    },
    {
      key: 'totalEarnings',
      header: t('affiliates.referralCodes.columns.totalEarnings'),
      numeric: true,
      render: row => formatCurrency(row.totalEarnings),
    },
    {
      key: 'created',
      header: t('affiliates.referralCodes.columns.created'),
      render: row => formatDate(row.createdDate) || t('common.none'),
    },
    {
      key: 'status',
      header: t('affiliates.referralCodes.columns.status'),
      render: row => (
        <StatusPill
          label={row.isActive ? t('status.active') : t('status.inactive')}
          tone={row.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: row => (
        <div className="ui-table__actions">
          <IconButton
            icon="edit"
            tone="indigo"
            disabled={pending}
            label={t('common.edit')}
            onClick={() => startEdit(row)}
          />
          <IconButton
            icon="trash"
            tone="rose"
            disabled={pending}
            label={t('common.delete')}
            onClick={() => setConfirmRemove(row)}
          />
        </div>
      ),
    },
  ];

  // Replaces this dialog rather than stacking on top of it, matching the
  // group-delete flow and avoiding two competing focus traps.
  if (confirmRemove) {
    return (
      <Modal
        open
        title={t('affiliates.referralCodes.deleteDialog.title')}
        description={confirmRemove.code}
        onClose={() => setConfirmRemove(null)}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setConfirmRemove(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={pending}
              onClick={() => remove(confirmRemove)}>
              {t('affiliates.referralCodes.deleteDialog.confirm')}
            </Button>
          </>
        }>
        <p>{t('affiliates.referralCodes.deleteDialog.description')}</p>
        <FormError variant="banner" message={submitError} />
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={t('affiliates.referralCodes.title')}
      description={affiliateEmail}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }>
      <p className="ui-modal__note">{t('affiliates.referralCodes.description')}</p>
      <FormError variant="banner" message={submitError} />

      <DataTable
        caption={t('affiliates.referralCodes.title')}
        columns={columns}
        rows={data?.codes ?? []}
        rowKey={row => row.id}
        status={status}
        error={error}
        onRetry={reload}
        emptyTitle={t('affiliates.referralCodes.emptyTitle')}
        emptyMessage={t('affiliates.referralCodes.emptyMessage')}
      />

      {data ? (
        <p className="ui-modal__note">
          {t('affiliates.referralCodes.totalEarningsNote', {
            amount: formatCurrency(data.earnings.totalEarnings),
          })}
        </p>
      ) : null}

      <div className="form-grid">
        <TextField
          label={t('affiliates.referralCodes.codeLabel')}
          placeholder={t('affiliates.referralCodes.codePlaceholder')}
          value={code}
          error={errors.code}
          onChange={event => setCode(event.target.value)}
        />
        <TextField
          label={t('affiliates.referralCodes.descriptionLabel')}
          placeholder={t('affiliates.referralCodes.descriptionPlaceholder')}
          value={description}
          onChange={event => setDescription(event.target.value)}
        />
        <TextField
          label={t('affiliates.referralCodes.commissionLabel')}
          type="number"
          min={0}
          step="0.01"
          value={commission}
          error={errors.commission}
          // The update endpoint does not accept a new commission, so the field
          // is read-only while editing rather than silently discarding a change.
          disabled={editing != null}
          onChange={event => setCommission(event.target.value)}
        />
      </div>
      <div className="form-actions">
        <Button disabled={pending} onClick={submit}>
          {editing
            ? t('affiliates.referralCodes.update')
            : t('affiliates.referralCodes.create')}
        </Button>
        {editing ? (
          <Button variant="secondary" disabled={pending} onClick={resetForm}>
            {t('affiliates.referralCodes.cancelEdit')}
          </Button>
        ) : null}
      </div>
    </Modal>
  );
}
